import { useEffect } from 'react';
import { create } from 'zustand';
import { type APIResponse } from '@room/shared-ui';
import { api } from '../lib/api';

interface CreditBalance {
  user_id: string;
  balance: number;
  total_deposited: number;
  total_spent: number;
  total_received: number;
  updated_at: number;
}

interface CreditsState {
  balance: number | null;
  fullData: CreditBalance | null;
  loading: boolean;
  lastUpdated: number | null;
  error: string | null;
}

interface CreditsActions {
  fetchCredits: () => Promise<void>;
  updateBalance: (newBalance: number) => void;
  reset: () => void;
}

const initialState: CreditsState = {
  balance: null,
  fullData: null,
  loading: false,
  lastUpdated: null,
  error: null,
};

export const useCreditsStore = create<CreditsState & CreditsActions>((set, get) => ({
  ...initialState,

  fetchCredits: async () => {
    set({ loading: true, error: null });

    try {
      const res = await api.get<APIResponse<CreditBalance>>('/credits/balance');

      if (res.success && res.data) {
        set({
          balance: res.data.balance,
          fullData: res.data,
          lastUpdated: Date.now(),
          loading: false,
          error: null,
        });

        window.dispatchEvent(
          new CustomEvent('credits:updated', {
            detail: { balance: res.data.balance, fullData: res.data },
          })
        );
      } else {
        set({
          loading: false,
          error: res.error || 'Failed to load credits',
        });
      }
    } catch (error) {
      set({
        loading: false,
        error: (error as Error)?.message || 'Network error',
      });
    }
  },

  updateBalance: (newBalance: number) => {
    const currentData = get().fullData;
    if (currentData) {
      set({
        balance: newBalance,
        fullData: { ...currentData, balance: newBalance },
        lastUpdated: Date.now(),
      });
    } else {
      set({ balance: newBalance });
    }

    window.dispatchEvent(
      new CustomEvent('credits:updated', {
        detail: { balance: newBalance },
      })
    );
  },

  reset: () => set(initialState),
}));

export type CreditEventType = 'deposit' | 'transfer' | 'refresh' | 'manual';

export interface CreditEventDetail {
  type: CreditEventType;
  amount?: number;
  timestamp: number;
}

export function triggerCreditUpdate(type: CreditEventType, amount?: number) {
  const event = new CustomEvent<CreditEventDetail>('credits:change', {
    detail: {
      type,
      amount,
      timestamp: Date.now(),
    },
  });

  window.dispatchEvent(event);

  const { fetchCredits } = useCreditsStore.getState();
  fetchCredits();
}

export function useCreditsListener(callback: (detail: CreditEventDetail) => void) {
  useEffect(() => {
    const handler = (event: Event) => {
      const customEvent = event as CustomEvent<CreditEventDetail>;
      callback(customEvent.detail);
    };

    window.addEventListener('credits:change', handler);
    return () => window.removeEventListener('credits:change', handler);
  }, [callback]);
}
